import * as React from "react"
import { CloudResourceType } from "@modules/graphql/types"

import {
  NotificationIntegrationsDocument,
  NotificationIntegrationsQuery,
  NotificationIntegrationsQueryVariables,
  useRemoveNotificationIntegrationMutation,
} from "@modules/notifications/deploys/queries.generated"

import { useShowSuccessToast } from "gatsby-interface"
import { notificationDeploys as notificationDeploysText } from "@modules/locales/default.json"

export const useRemoveNotificationIntegration = ({
  resourceId,
  resourceType,
}: {
  resourceId: string
  resourceType: CloudResourceType
}) => {
  const [
    mutate,
    { loading, error: mutationError },
  ] = useRemoveNotificationIntegrationMutation()
  const [customError, setCustomError] = React.useState<Error | null>(null)

  const showSuccessToast = useShowSuccessToast()

  const removeNotificationIntegration = (integrationId: string) =>
    mutate({
      variables: {
        integrationId,
      },
      update: (proxy, { data }) => {
        if (!data?.removeNotificationIntegration?.success) {
          return
        }
        const queryInfo = {
          query: NotificationIntegrationsDocument,
          variables: {
            resourceId,
            resourceType,
          },
        }
        const queryData = proxy.readQuery<
          NotificationIntegrationsQuery,
          NotificationIntegrationsQueryVariables
        >(queryInfo)

        proxy.writeQuery<
          NotificationIntegrationsQuery,
          NotificationIntegrationsQueryVariables
        >({
          ...queryInfo,
          data: {
            ...queryData,
            notificationIntegrations: queryData?.notificationIntegrations?.filter(
              ({ id }) => id !== integrationId
            ),
          },
        })
      },
    })
      .then(res => {
        if (res?.data?.removeNotificationIntegration?.success) {
          showSuccessToast(
            notificationDeploysText.messages.notificationSuccessfullyRemoved
          )
        } else {
          throw new Error(
            res?.data?.removeNotificationIntegration?.message ??
              notificationDeploysText.messages.failedToRemoveNotification
          )
        }
      })
      .catch(setCustomError)

  return [
    removeNotificationIntegration,
    { loading, error: customError || mutationError },
  ] as const
}
